import {
    growaveConfigurationToken,
    localizationInfoProviderToken,
    shopBrandInfoProviderToken,
} from "@/tokens";
import RewardsSpendingRulesApiService from "@apps/rewards/api_services/RewardsSpendingRulesApiService/RewardsSpendingRulesApiService";
import RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
import RewardsCustomerActiveRewardsQuery from "@apps/rewards/modules/CustomerRewards/queries/RewardsCustomerActiveRewardsQuery";
import RewardsEarningRulesQuery from "@apps/rewards/modules/EarningRules/query/RewardsEarningRulesQuery";
import RewardsReferralProgramQuery from "@apps/rewards/modules/ReferralProgram/queries/RewardsReferralProgramQuery";
import RewardsBoostCampaignQuery from "@apps/rewards/queries/RewardsBoostCampaignQuery";
import RewardsCustomerBirthdayQuery from "@apps/rewards/queries/RewardsCustomerBirthdayQuery";
import RewardsNewsletterSubscriptionQuery from "@apps/rewards/queries/RewardsNewsletterSubscriptionQuery";
import RewardsSpendingRulesQuery from "@apps/rewards/queries/RewardsSpendingRulesQuery";
import RewardsVerificationTokenQuery from "@apps/rewards/queries/RewardsVerificationTokenQuery";
import RewardsPopUpWidgetLauncherManager from "@apps/rewards/widgets/rewards_popup_widget/utils/RewardsPopUpWidgetLauncherManager/RewardsPopUpWidgetLauncherManager";
import { InjectionKey } from "@lib/svelte-typed-context";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { ROLE } from "@modules/authentication/constants";
import { CartModule } from "@modules/cart/CartModule";
import { CommonModule } from "@modules/common_module/CommonModule";
import { ShopifyDiscountModule } from "@modules/discount/ShopifyDiscountModule";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { I18nModule } from "@modules/i18n/I18nModule";
import { MoneyModule } from "@modules/money/MoneyModule";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import { map } from "rxjs/operators";
import { container } from "tsyringe";

import { RewardsTranslationNamespaces } from "./constants/constants";
import { rewardsContextToken } from "./context_tokens";
import RewardsCurrencyFormatter from "./lib/RewardsCurrencyFormatter";
import RewardsCustomerDelayedRewardsQuery from "./modules/CustomerRewards/queries/RewardsCustomerDelayedRewardsQuery";
import RewardsCustomerRewardsDiscountService from "./modules/CustomerRewards/services/RewardsCustomerRewardsDiscountService";
import RewardsCustomerRewardsService from "./modules/CustomerRewards/services/RewardsCustomerRewardsService";
import RewardsEarningRulesService from "./modules/EarningRules/service/RewardsEarningRulesService";
import RewardsFaqQuery from "./modules/Faq/query/RewardsFaqQuery";
import RewardsFaqService from "./modules/Faq/service/RewardsFaqService";
import RewardsHistoryQuery from "./modules/History/query/RewardsHistoryQuery";
import RewardsHistoryService from "./modules/History/service/RewardsHistoryService";
import RewardsProgramAccountQuery from "./modules/ProgramAccount/query/RewardsProgramAccountQuery";
import RewardsProgramAccountService from "./modules/ProgramAccount/service/RewardsProgramAccountService";
import RewardsQRCodeQuery from "./modules/QRCode/RewardsQRCodeQuery";
import RewardsQRCodeService from "./modules/QRCode/RewardsQRCodeService";
import RewardsReferralProgramService from "./modules/ReferralProgram/services/RewardsReferralProgramService";
import RewardsVipTierProgressQuery from "./modules/VipTiers/queries/RewardsVipTierProgressQuery";
import RewardsVipTiersQuery from "./modules/VipTiers/queries/RewardsVipTiersQuery";
import RewardsVipTiersService from "./modules/VipTiers/service/RewardsVipTiersService";
import RewardsBoostCampaignService from "./services/RewardsBoostCampaignService";
import RewardsCustomerBirthdayService from "./services/RewardsCustomerBirthdayService";
import RewardsCustomerEligibilityService from "./services/RewardsCustomerEligibilityService/RewardsCustomerEligibilityService";
import RewardsNewsletterSubscriptionService from "./services/RewardsNewsletterSubscriptionService";
import RewardsStorefrontValuesService from "./services/RewardsStorefrontValuesService";
import RewardsTrackEventService from "./services/RewardsTrackEventService/RewardsTrackEventService";
import RewardsVerificationTokenService from "./services/RewardsVerificationTokenService";
import RewardsModalManager from "./utils/RewardsModalManager/RewardsModalManager";
import RewardsPopUpRootWidgetCardManager from "./widgets/rewards_popup_root_widget/utils/RewardsPopUpWidgetCardManager/RewardsPopUpRootWidgetCardManager";

let context: Map<InjectionKey<string>, typeof rewardsContextToken>;

export const getRewardsContext = () => {
    if (!context) {
        const {
            locationController,
            urlsBuilder,
            applyDiscountAvailabilityProvider,
            cartAvailabilityProvider,
            designModeProvider,
        } = container.resolve(CommonModule).exports;

        const { cartService, cartQuery } =
            container.resolve(CartModule).exports;
        const { shopifyDiscountApiService, shopifyDiscountQuery } =
            container.resolve(ShopifyDiscountModule).exports;
        const { i18n } = container.resolve(I18nModule).exports;
        const { htmlSanitizer } = container.resolve(MoneyModule).exports;

        context = new Map([
            [
                rewardsContextToken,
                {
                    //programAccount
                    rewardsProgramAccountService: container.resolve(
                        RewardsProgramAccountService
                    ),
                    rewardsProgramAccountQuery: container.resolve(
                        RewardsProgramAccountQuery
                    ),

                    //referralProgram
                    rewardsReferralProgramService: container.resolve(
                        RewardsReferralProgramService
                    ),
                    rewardsReferralProgramQuery: container.resolve(
                        RewardsReferralProgramQuery
                    ),

                    //earningRules
                    rewardsEarningRulesService: container.resolve(
                        RewardsEarningRulesService
                    ),
                    rewardsEarningRulesQuery: container.resolve(
                        RewardsEarningRulesQuery
                    ),

                    //spendingRules
                    rewardsSpendingRulesApiService: container.resolve(
                        RewardsSpendingRulesApiService
                    ),
                    rewardsSpendingRulesQuery: container.resolve(
                        RewardsSpendingRulesQuery
                    ),

                    //faq
                    rewardsFaqService: container.resolve(RewardsFaqService),
                    rewardsFaqQuery: container.resolve(RewardsFaqQuery),

                    //vipTiers
                    rewardsVipTiersService: container.resolve(
                        RewardsVipTiersService
                    ),
                    rewardsVipTiersQuery:
                        container.resolve(RewardsVipTiersQuery),
                    rewardsVipTierProgressQuery: container.resolve(
                        RewardsVipTierProgressQuery
                    ),

                    //customerRewards
                    rewardsCustomerRewardsService: container.resolve(
                        RewardsCustomerRewardsService
                    ),
                    rewardsCustomerRewardsDiscountService: container.resolve(
                        RewardsCustomerRewardsDiscountService
                    ),
                    rewardsCustomerActiveRewardsQuery: container.resolve(
                        RewardsCustomerActiveRewardsQuery
                    ),
                    rewardsCustomerDelayedRewardsQuery: container.resolve(
                        RewardsCustomerDelayedRewardsQuery
                    ),

                    //boostCampaign
                    rewardsBoostCampaignService: container.resolve(
                        RewardsBoostCampaignService
                    ),
                    rewardsBoostCampaignQuery: container.resolve(
                        RewardsBoostCampaignQuery
                    ),

                    //customerBirthday
                    rewardsCustomerBirthdayService: container.resolve(
                        RewardsCustomerBirthdayService
                    ),
                    rewardsCustomerBirthdayQuery: container.resolve(
                        RewardsCustomerBirthdayQuery
                    ),

                    //newsletterSubscription
                    rewardsNewsletterSubscriptionService: container.resolve(
                        RewardsNewsletterSubscriptionService
                    ),
                    rewardsNewsletterSubscriptionQuery: container.resolve(
                        RewardsNewsletterSubscriptionQuery
                    ),

                    //history
                    rewardsHistoryService: container.resolve(
                        RewardsHistoryService
                    ),
                    rewardsHistoryQuery: container.resolve(RewardsHistoryQuery),

                    //tokenManagerModule
                    tokenManagerModule: container.resolve(TokenManagerModule),

                    //qrCode
                    rewardsQRCodeService:
                        container.resolve(RewardsQRCodeService),
                    rewardsQRCodeQuery: container.resolve(RewardsQRCodeQuery),

                    //verificationToken
                    rewardsVerificationTokenService: container.resolve(
                        RewardsVerificationTokenService
                    ),
                    rewardsVerificationTokenQuery: container.resolve(
                        RewardsVerificationTokenQuery
                    ),

                    //customerEligibilityService
                    rewardsCustomerEligibilityService: container.resolve(
                        RewardsCustomerEligibilityService
                    ),

                    //trackEvent
                    rewardsTrackEventService: container.resolve(
                        RewardsTrackEventService
                    ),

                    //rewardsAmountFormatter
                    rewardsAmountFormatter: container.resolve(
                        RewardsAmountFormatter
                    ),

                    //locationController
                    locationController,

                    //eventsCollector
                    eventsCollector: container.resolve(
                        AnalyticsEventsCollectorModule
                    ).exports.eventsCollector,

                    //cartService
                    cartService,
                    cartQuery,

                    //isCustomer
                    isCustomer$: container
                        .resolve(AuthenticationModule)
                        .exports.authenticationInfoProvider.selectRole()
                        .pipe(map((role) => role === ROLE.Customer)),

                    //namespace
                    rewardsPopUpWidgetNamespace:
                        RewardsTranslationNamespaces.rewardsPopUpWidget,

                    //modalManager
                    rewardsModalManager: container.resolve(RewardsModalManager),

                    //shopifyDiscount
                    shopifyDiscountApiService,
                    shopifyDiscountQuery,

                    //dynamicImportManager
                    dynamicImportManager:
                        container.resolve(DynamicImportModule).exports
                            .dynamicImportManager,

                    //launcherManager
                    rewardsPopUpWidgetLauncherManager: container.resolve(
                        RewardsPopUpWidgetLauncherManager
                    ),

                    //cardManager
                    rewardsPopUpRootWidgetCardManager: container.resolve(
                        RewardsPopUpRootWidgetCardManager
                    ),

                    //gwI18n
                    gwI18n: i18n,

                    //localizationInfoProvider
                    localizationInfoProvider: container.resolve(
                        localizationInfoProviderToken
                    ),

                    //pointsName
                    pointsName$: i18n.selectT(
                        "common.critical.Points",
                        "points_name_plural"
                    ),

                    //rewardsCurrencyFormatter
                    rewardsCurrencyFormatter: container.resolve(
                        RewardsCurrencyFormatter
                    ),

                    //sanitizer
                    sanitizer: htmlSanitizer,

                    //growaveConfiguration
                    growaveConfiguration: container.resolve(
                        growaveConfigurationToken
                    ),

                    //urlsBuilder
                    urlsBuilder,

                    //applyDiscountAvailabilityProvider
                    applyDiscountAvailabilityProvider,

                    //cartAvailabilityProvider
                    cartAvailabilityProvider,

                    //rewardsStorefrontValues
                    rewardsStorefrontValuesService: container.resolve(
                        RewardsStorefrontValuesService
                    ),

                    // shopBrandInfoProvider
                    shopBrandInfoProvider: container.resolve(
                        shopBrandInfoProviderToken
                    ),

                    //designModeProvider
                    designModeProvider,
                },
            ],
        ]);
    }

    return context;
};
